<template>
  <div class="rounded-md border p-6">
    <div class="mb-4 flex items-center justify-between">
      <div>
        <h3 class="text-lg font-semibold">Backtest Results</h3>
        <p class="text-sm text-muted-foreground">
          Historical performance over {{ weeks }} weeks
        </p>
      </div>
      <div class="flex items-center gap-2">
        <button
          v-if="!loading"
          @click="runBacktest"
          class="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {{ backtest ? 'Refresh' : 'Run Backtest' }}
        </button>
        <button
          v-if="loading"
          @click="cancelBacktest"
          class="rounded-md bg-destructive px-3 py-1.5 text-sm font-medium text-destructive-foreground hover:bg-destructive/90"
        >
          Cancel
        </button>
        <div v-if="loading" class="flex items-center gap-2">
          <svg
            class="h-4 w-4 animate-spin text-muted-foreground"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span class="text-sm text-muted-foreground">{{ statusMessage || 'Running...' }}</span>
        </div>
      </div>
    </div>

    <!-- Progress Bar -->
    <div v-if="loading && progress > 0" class="mb-4">
      <div class="mb-2 flex items-center justify-between text-sm">
        <span class="text-muted-foreground">Progress</span>
        <div class="flex items-center gap-3">
          <span class="font-medium">{{ progress }}%</span>
          <span v-if="etaSeconds !== null && etaSeconds > 0" class="text-xs text-muted-foreground">
            ETA: {{ formatETA(etaSeconds) }}
          </span>
        </div>
      </div>
      <div class="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          class="h-full bg-primary transition-all duration-300"
          :style="{ width: `${progress}%` }"
        ></div>
      </div>
    </div>

    <div v-if="error" class="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
      {{ error }}
    </div>

    <div v-else-if="backtest" class="space-y-4">
      <!-- Summary Stats -->
      <div class="grid grid-cols-2 gap-4">
        <div class="rounded-md bg-muted/50 p-3">
          <p class="text-xs text-muted-foreground">Directional Accuracy</p>
          <p class="text-lg font-semibold">
            {{ (backtest.metrics.directional_accuracy * 100).toFixed(1) }}%
          </p>
        </div>
        <div class="rounded-md bg-muted/50 p-3">
          <p class="text-xs text-muted-foreground">MAPE</p>
          <p class="text-lg font-semibold">{{ (backtest.metrics.mape * 100).toFixed(2) }}%</p>
        </div>
        <div class="rounded-md bg-muted/50 p-3">
          <p class="text-xs text-muted-foreground">RMSE</p>
          <p class="text-lg font-semibold">${{ backtest.metrics.rmse.toFixed(2) }}</p>
        </div>
        <div class="rounded-md bg-muted/50 p-3">
          <p class="text-xs text-muted-foreground">Hit Rate (Q20-Q80)</p>
          <p class="text-lg font-semibold">
            {{ (backtest.metrics.hit_rate_q20_q80 * 100).toFixed(1) }}%
          </p>
        </div>
      </div>

      <!-- Additional Metrics -->
      <div class="space-y-2 rounded-md bg-muted/50 p-3">
        <div class="flex items-center justify-between text-sm">
          <span class="text-muted-foreground">Precision @ 1.5% Threshold</span>
          <span class="font-medium">
            {{ (backtest.metrics.precision_at_k * 100).toFixed(1) }}%
          </span>
        </div>
        <div class="flex items-center justify-between text-sm">
          <span class="text-muted-foreground">Mean Error</span>
          <span class="font-medium">
            {{ (backtest.metrics.mean_error * 100).toFixed(2) }}%
          </span>
        </div>
        <div class="flex items-center justify-between text-sm">
          <span class="text-muted-foreground">High Confidence Predictions</span>
          <span class="font-medium">{{ backtest.metrics.high_confidence_count }}</span>
        </div>
        <div class="flex items-center justify-between text-sm">
          <span class="text-muted-foreground">Total Predictions</span>
          <span class="font-medium">{{ backtest.metrics.n_predictions }}</span>
        </div>
      </div>
    </div>

    <div v-else class="rounded-md bg-muted/50 p-8 text-center">
      <svg
        class="mx-auto h-12 w-12 text-muted-foreground"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
        <path d="M16 13H8" />
        <path d="M16 17H8" />
        <path d="M10 9H8" />
      </svg>
      <p class="mt-4 text-sm text-muted-foreground">Loading backtest data...</p>
      <p class="mt-1 text-xs text-muted-foreground">Analyzing historical performance</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onUnmounted } from 'vue'
import { apiClient, type BacktestResponse, type BacktestJobStatusResponse } from '@/lib/api'

interface Props {
  symbol: string
  weeks?: number
}

const props = withDefaults(defineProps<Props>(), {
  weeks: 104
})

const backtest = ref<BacktestResponse | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const progress = ref(0)
const statusMessage = ref('')
const etaSeconds = ref<number | null>(null)
let websocket: WebSocket | null = null
let jobId: string | null = null
let pollInterval: ReturnType<typeof setInterval> | null = null

const formatETA = (seconds: number): string => {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60)
    const secs = Math.round(seconds % 60)
    return `${minutes}m ${secs}s`
  } else {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${hours}h ${minutes}m`
  }
}

const connectWebSocket = (jobId: string) => {
  // Determine WebSocket URL - use same host/port as API
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  // In development, Vue runs on 5173 but API is on 8000
  const isDev = window.location.hostname === 'localhost' && window.location.port === '5173'
  const apiHost = isDev ? 'localhost:8000' : window.location.host
  const wsUrl = `${protocol}//${apiHost}/api/backtest/ws/${jobId}`
  
  console.log('Connecting to WebSocket:', wsUrl)
  
  websocket = new WebSocket(wsUrl)
  
  websocket.onopen = () => {
    console.log('WebSocket connected for job:', jobId)
    statusMessage.value = 'Connected, running backtest...'
  }
  
  websocket.onmessage = (event) => {
    try {
      const data: BacktestJobStatusResponse = JSON.parse(event.data)
      progress.value = data.progress
      statusMessage.value = data.message || 'Processing...'
      etaSeconds.value = data.eta_seconds ?? null
      
      if (data.status === 'completed' && data.result) {
        backtest.value = data.result
        loading.value = false
        progress.value = 100
        statusMessage.value = 'Completed successfully'
        etaSeconds.value = null
        websocket?.close()
        websocket = null
      } else if (data.status === 'failed') {
        error.value = data.error || 'Backtest failed'
        loading.value = false
        etaSeconds.value = null
        websocket?.close()
        websocket = null
      } else if (data.status === 'cancelled') {
        error.value = 'Backtest was cancelled'
        loading.value = false
        progress.value = data.progress
        statusMessage.value = 'Cancelled'
        etaSeconds.value = null
        websocket?.close()
        websocket = null
      }
    } catch (err) {
      console.error('Error parsing WebSocket message:', err)
    }
  }
  
  websocket.onerror = (err) => {
    console.error('WebSocket error:', err)
    // Fallback to polling if WebSocket fails
    console.log('Falling back to polling mode')
    pollJobStatus(jobId)
  }
  
  websocket.onclose = () => {
    console.log('WebSocket closed')
    websocket = null
  }
}

const pollJobStatus = async (jobId: string) => {
  if (pollInterval) {
    clearInterval(pollInterval)
  }
  
  pollInterval = setInterval(async () => {
    try {
      const status = await apiClient.getBacktestJobStatus(jobId)
      progress.value = status.progress
      statusMessage.value = status.message || 'Processing...'
      etaSeconds.value = status.eta_seconds ?? null
      
      if (status.status === 'completed' && status.result) {
        backtest.value = status.result
        loading.value = false
        progress.value = 100
        statusMessage.value = 'Completed successfully'
        etaSeconds.value = null
        if (pollInterval) {
          clearInterval(pollInterval)
          pollInterval = null
        }
      } else if (status.status === 'failed') {
        error.value = status.error || 'Backtest failed'
        loading.value = false
        etaSeconds.value = null
        if (pollInterval) {
          clearInterval(pollInterval)
          pollInterval = null
        }
      } else if (status.status === 'cancelled') {
        error.value = 'Backtest was cancelled'
        loading.value = false
        progress.value = status.progress
        statusMessage.value = 'Cancelled'
        etaSeconds.value = null
        if (pollInterval) {
          clearInterval(pollInterval)
          pollInterval = null
        }
      }
    } catch (err) {
      console.error('Error polling job status:', err)
      if (pollInterval) {
        clearInterval(pollInterval)
        pollInterval = null
      }
      loading.value = false
      error.value = 'Failed to get job status'
    }
  }, 2000) // Poll every 2 seconds
}

const runBacktest = async () => {
  loading.value = true
  error.value = null
  progress.value = 0
  statusMessage.value = 'Starting backtest...'
  backtest.value = null
  etaSeconds.value = null
  
  try {
    // Start the backtest job
    const jobResponse = await apiClient.runBacktest(props.symbol, props.weeks)
    jobId = jobResponse.job_id
    
    // Connect to WebSocket for real-time updates
    connectWebSocket(jobId)
  } catch (err: any) {
    error.value = err.response?.data?.detail || err.message || 'Failed to start backtest'
    loading.value = false
    etaSeconds.value = null
    console.error('Backtest error:', err)
  }
}

const cancelBacktest = async () => {
  if (!jobId) return
  
  try {
    await apiClient.cancelBacktest(jobId)
    statusMessage.value = 'Cancelling...'
    // The WebSocket or polling will update the status to cancelled
  } catch (err: any) {
    error.value = err.response?.data?.detail || err.message || 'Failed to cancel backtest'
    console.error('Cancel error:', err)
  }
}

onUnmounted(() => {
  if (websocket) {
    websocket.close()
    websocket = null
  }
  if (pollInterval) {
    clearInterval(pollInterval)
    pollInterval = null
  }
})
</script>


